import { useTranslation } from "react-i18next";import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { tenancyAPI } from '../services/api';

const TenantContext = createContext(null);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }) => {const { t } = useTranslation("common");
  const { isAuthenticated, token } = useAuth();
  const [availableScopes, setAvailableScopes] = useState({ tenants: [] });
  const [activeContext, setActiveContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSelector, setShowSelector] = useState(false);

  // 1. Load Available Scopes & Active Context
  const loadContext = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      const [scopes, current] = await Promise.all([
      tenancyAPI.getAvailableScopes(),
      tenancyAPI.getCurrentContext()]
      );

      setAvailableScopes(scopes);
      setActiveContext(current);

      // Store effective context IDs if not set
      if (!localStorage.getItem('ezerp_tenant_id') && current.client_id) {
        localStorage.setItem('ezerp_tenant_id', current.client_id.toString());
      }
      if (!localStorage.getItem('ezerp_org_id') && current.business_id) {
        localStorage.setItem('ezerp_org_id', current.business_id.toString());
      }

      // Proactive: If user has multiple options and hasn't picked, show selector
      const storedOrgId = localStorage.getItem('ezerp_org_id');
      if (isAuthenticated && !storedOrgId) {
        // Only show if there's actually something to select
        if (scopes.tenants.length > 0) {
          setShowSelector(true);
        }
      }
    } catch (error) {
      console.error('Failed to load tenancy context:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // 2. Context Switching (Phase 2 Design: Full Reload)
  const switchContext = (tenantId, orgId, redirectTo = null) => {
    if (tenantId) localStorage.setItem('ezerp_tenant_id', tenantId.toString());
    if (orgId) localStorage.setItem('ezerp_org_id', orgId.toString());else
    localStorage.removeItem('ezerp_org_id'); // If switching to tenant-only scope

    // Force full app reload to ensure clean cache and consistent context injection
    if (redirectTo) {
      window.location.href = redirectTo;
    } else {
      window.location.reload();
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadContext();
    } else {
      setAvailableScopes({ tenants: [] });
      setActiveContext(null);
      setLoading(false);
    }
  }, [isAuthenticated, loadContext]);

  const value = {
    availableScopes,
    activeContext,
    loading,
    showSelector,
    setShowSelector,
    switchContext,
    refreshContext: loadContext,

    // Legacy mapping for Phase 1 compatibility
    businesses: activeContext?.allowed_business_ids || [],
    currentBusiness: activeContext?.business_id ?
    { id: activeContext.business_id, name: activeContext.business_name } :
    null,

    // Access checks
    isPlatformAdmin: activeContext?.is_platform_user || false,
    allowedModules: activeContext?.allowed_modules || [],
    canAccessModule: (code) => activeContext?.is_platform_user || activeContext?.allowed_modules?.includes(code)
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>);

};

export default TenantContext;